import { useCallback } from 'react';

import { ApolloError } from 'apollo-client';
import { getOperationName } from 'apollo-link';

import { OptionalProps } from '@rbi-ctg/frontend';
import {
  GetPrepaidsBalanceDocument,
  IGetPrepaidsBalanceMutationVariables,
  IsoCountryCode,
  useGetPrepaidsBalanceMutation,
} from 'generated/graphql-gateway';
import {
  GetPrepaidsBalanceDocument as WLGetPrepaidsBalanceDocument,
  useGetPrepaidsBalanceMutation as useWLGetPrepaidsBalanceMutation,
} from 'generated/rbi-graphql';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';

import { IUsePrepaidsBalanceProps } from './types';

type Variables = IGetPrepaidsBalanceMutationVariables;

const WLMutationName = getOperationName(WLGetPrepaidsBalanceDocument)!;
const MutationName = getOperationName(GetPrepaidsBalanceDocument)!;
/*
 * usePrepaidsBalance uses schema generated from WL and gateway
 * WL code and Flag check should be removed once prepaidsBalance is fully migrated to gateway
 */
export const usePrepaidsBalance = ({ region }: IUsePrepaidsBalanceProps) => {
  const enablePinValidation = useFlag(LaunchDarklyFlag.ENABLE_PIN_VALIDATION);
  const enableGatewayPrepaidsBalance = useFlag(LaunchDarklyFlag.ENABLE_GATEWAY_PREPAIDS_BALANCE);
  const enableGatewayPinValidation = enablePinValidation && enableGatewayPrepaidsBalance;

  const logger = useLogger();
  const onError = useCallback(
    (error: ApolloError) => {
      const { graphQLErrors: errors } = error;
      const msg = 'Error retrieving prepaid balance';
      if (errors.length) {
        logger.error({
          errors,
          message: enableGatewayPinValidation ? `${msg} - gateway` : `${msg} - WL`,
          queryName: enableGatewayPinValidation ? MutationName : WLMutationName,
        });
      }
    },
    [enableGatewayPinValidation, logger]
  );

  const [fireMutation, mutationResult] = useGetPrepaidsBalanceMutation({ onError });
  const [fireWLMutation, WLMutationResult] = useWLGetPrepaidsBalanceMutation({ onError });

  const getPrepaidsBalance = useCallback(
    ({
      cardNumber,
      feCountryCode = IsoCountryCode[region],
      pin = '',
    }: OptionalProps<Variables, 'feCountryCode'>) => {
      return enableGatewayPinValidation
        ? fireMutation({ variables: { cardNumber, feCountryCode, pin } })
        : fireWLMutation({ variables: { cardNumber, feCountryCode } });
    },
    [region, enableGatewayPinValidation, fireMutation, fireWLMutation]
  );

  if (enableGatewayPinValidation) {
    const { data } = mutationResult;
    const currentBalance =
      data && data.prepaidsBalance ? data.prepaidsBalance.currentBalance : null;
    return { ...mutationResult, currentBalance, getPrepaidsBalance };
  }

  const { data } = WLMutationResult;
  const currentBalance = data && data.prepaidsBalance ? data.prepaidsBalance.currentBalance : null;
  return { ...WLMutationResult, currentBalance, getPrepaidsBalance };
};
